import type { ColumnDefinition } from '@/components/atomic/table/types'

import { RequiredFormFieldsForOrg } from '@/app/feature-flags'
import {
  ASSET_CLASS_COLUMN,
  ASSET_TYPE_COLUMN,
} from '@/components/atomic/table/columns'
import * as TableColumns from '@/shared/helpers/trade-blotter-columns'
import { TradeBlotterColumnId } from '@/types/enums/trade-blotter'

const tradeBlotterColumnDefinitions: Map<
  TradeBlotterColumnId,
  TableColumns.TradeBlotterColumnDefinition
> = new Map([
  // TIME - First column, sorted descending
  [
    TradeBlotterColumnId.TIME,
    TableColumns.TIME.create({ hide: false, sort: 'desc' }),
  ],
  // SIDE - Buy/Sell indicator (matches Trade-Desk position 2)
  [TradeBlotterColumnId.SIDE, TableColumns.ORDER_SIDE.create({ hide: false })],
  // IDENTIFIER - Security identifier (Trade-Desk blotter uses CUSIP column instead;
  // CUSIP always shows CUSIP, whereas IDENTIFIER respects user preference for CUSIP/ISIN)
  [
    TradeBlotterColumnId.IDENTIFIER,
    TableColumns.ORDER_IDENTIFIER.create({ hide: false }),
  ],
  // ASSET_TYPE - High-level asset type (Fixed Income / Equity); gated by equities-UI flag
  [
    TradeBlotterColumnId.ASSET_TYPE,
    ASSET_TYPE_COLUMN({
      colId: TradeBlotterColumnId.ASSET_TYPE,
      hide: false,
      width: 90,
    }),
  ],
  // BOND_TYPE - Bond type classification
  [
    TradeBlotterColumnId.BOND_TYPE,
    ASSET_CLASS_COLUMN({
      colId: TradeBlotterColumnId.BOND_TYPE,
      hide: false,
      width: 90,
    }),
  ],
  // DESCRIPTION - Full bond description
  [
    TradeBlotterColumnId.DESCRIPTION,
    TableColumns.ORDER_DESCRIPTION.create({ hide: false }),
  ],
  // STATUS - Order status (Advisor view)
  [
    TradeBlotterColumnId.STATUS,
    TableColumns.ADVISOR_VIEW_ORDER_STATUS.create({ hide: false }),
  ],
  // ALLOCATION_STATUS - Allocation progress
  [
    TradeBlotterColumnId.ALLOCATION_STATUS,
    TableColumns.ORDER_ALLOCATION_STATUS.create({ hide: false }),
  ],
  // ORDER_PROGRESS - Order fill progress bar
  [
    TradeBlotterColumnId.ORDER_PROGRESS,
    TableColumns.ORDER_PROGRESS.create({ hide: false }),
  ],
  // ACCOUNT - Account identifier
  [
    TradeBlotterColumnId.ACCOUNT,
    TableColumns.ORDER_ACCOUNT.create({ hide: false }),
  ],
  // PRIME_BROKERAGE_ENABLED - Prime brokerage flag (conditional)
  [
    TradeBlotterColumnId.PRIME_BROKERAGE_ENABLED,
    TableColumns.ORDER_PRIME_BROKERAGE_ENABLED.create({ hide: false }),
  ],
  // CUSTODIAN - Custodian information
  [
    TradeBlotterColumnId.CUSTODIAN,
    TableColumns.ORDER_CUSTODIAN.create({ hide: false }),
  ],
  // ICON_ANNOTATION - Visual indicators/annotations
  [
    TradeBlotterColumnId.ICON_ANNOTATION,
    TableColumns.ORDER_ICON_ANNOTATION.create({ hide: false }),
  ],
  // LIMIT_PRICE - Displays 'Market' for market orders, price for limit orders
  // (same logic as Trade-Desk; replaces separate TYPE column)
  [
    TradeBlotterColumnId.LIMIT_PRICE,
    TableColumns.ORDER_LIMIT_PRICE.create({ hide: false }),
  ],
  // QUANTITY - Order quantity
  [
    TradeBlotterColumnId.QUANTITY,
    TableColumns.ORDER_QUANTITY.create({ hide: false }),
  ],
  // FILLED - Filled quantity
  [
    TradeBlotterColumnId.FILLED,
    TableColumns.ORDER_FILLED.create({ hide: false }),
  ],
  // FILLED_AVG_PRICE - Average fill price
  [
    TradeBlotterColumnId.FILLED_AVG_PRICE,
    TableColumns.ORDER_FILLED_AVG_PRICE.create({ hide: false }),
  ],
  // YTW - Yield to Worst (filled)
  [
    TradeBlotterColumnId.YTW,
    TableColumns.ORDER_YTW.create({
      colId: TradeBlotterColumnId.YTW,
      hide: false,
    }),
  ],
  // SETTLEMENT_DATE - Order settlement date (conditional)
  [
    TradeBlotterColumnId.SETTLEMENT_DATE,
    {
      ...TableColumns.ORDER_SETTLEMENT_DATE.create({ hide: false }),
      colId: TradeBlotterColumnId.SETTLEMENT_DATE,
    },
  ],
  // ACCRUED_INTEREST - Accrued interest on the order
  [
    TradeBlotterColumnId.ACCRUED_INTEREST,
    TableColumns.ORDER_ACCRUED_INTEREST.create({ hide: false }),
  ],
  // NET_MONEY - Net money for the filled order
  [
    TradeBlotterColumnId.NET_MONEY,
    TableColumns.ORDER_NET_MONEY.create({ hide: false }),
  ],
  // IB_NAME - Introducing broker name (Organization)
  [
    TradeBlotterColumnId.IB_NAME,
    TableColumns.ORDER_IB_NAME.create({ hide: false }),
  ],
  // REJECT_REASON - Order rejection reason
  [
    TradeBlotterColumnId.REJECT_REASON,
    TableColumns.ORDER_REJECT_REASON.create({ hide: false }),
  ],
  // CANCEL_REASON - Order cancellation reason
  [
    TradeBlotterColumnId.CANCEL_REASON,
    TableColumns.ORDER_CANCEL_REASON.create({ hide: false }),
  ],
  // ID - Moment Order ID
  [TradeBlotterColumnId.ID, TableColumns.ORDER_ID.create({ hide: false })],
  // CLIENT_ORDER_ID - Client's order ID (hidden by default)
  [
    TradeBlotterColumnId.CLIENT_ORDER_ID,
    TableColumns.CLIENT_ORDER_ID.create({ hide: true }),
  ],
  // EXTENDED_HOURS - Extended hours trading flag
  [
    TradeBlotterColumnId.EXTENDED_HOURS,
    TableColumns.ORDER_EXTENDED_HOURS.create({ hide: false }),
  ],
  // HAS_DISCRETIONARY_AUTHORITY - Discretionary authority flag (conditional)
  [
    TradeBlotterColumnId.HAS_DISCRETIONARY_AUTHORITY,
    TableColumns.ORDER_HAS_DISCRETIONARY_AUTHORITY.create({ hide: false }),
  ],
  // SUBMISSION_NAME - Submission identifier
  [
    TradeBlotterColumnId.SUBMISSION_NAME,
    TableColumns.ORDER_SUBMISSION_NAME.create({ hide: false }),
  ],
  // LIST_RFQ_NAME - List RFQ name (conditional on feature flag)
  [
    TradeBlotterColumnId.LIST_RFQ_NAME,
    TableColumns.ORDER_LIST_RFQ_NAME.create({ hide: false }),
  ],
])

export const getColumnsToHide = (config: {
  showPrimeBrokerageColumn: boolean
  isListRFQEnabled?: boolean
  requiredFormFieldsForOrg?: RequiredFormFieldsForOrg[]
  isEquitiesUiEnabled?: boolean
}): TradeBlotterColumnId[] => {
  const {
    showPrimeBrokerageColumn,
    isListRFQEnabled,
    requiredFormFieldsForOrg,
    isEquitiesUiEnabled,
  } = config

  const columnsToHide: TradeBlotterColumnId[] = []

  if (!isEquitiesUiEnabled) {
    columnsToHide.push(TradeBlotterColumnId.ASSET_TYPE)
  }

  if (!showPrimeBrokerageColumn) {
    columnsToHide.push(TradeBlotterColumnId.PRIME_BROKERAGE_ENABLED)
  }

  if (
    !requiredFormFieldsForOrg?.includes(
      RequiredFormFieldsForOrg.HasDiscretionaryAuthority,
    )
  ) {
    columnsToHide.push(TradeBlotterColumnId.HAS_DISCRETIONARY_AUTHORITY)
  }

  if (!isListRFQEnabled) {
    columnsToHide.push(TradeBlotterColumnId.LIST_RFQ_NAME)
  }

  // The settlement-date column is not surfaced on the advisor blotter.
  columnsToHide.push(TradeBlotterColumnId.SETTLEMENT_DATE)

  return columnsToHide
}

export const getTradeBlotterColumnDefinitions = (
  columnsToHide: TradeBlotterColumnId[],
  useProposedLayout = false,
): ColumnDefinition[] => {
  const columnsToHideSet = new Set(columnsToHide)
  const columnsShown = Array.from(
    tradeBlotterColumnDefinitions.values(),
  ).filter(
    (column) =>
      column.colId &&
      !columnsToHideSet.has(column.colId as TradeBlotterColumnId),
  )
  const withLayouts = TableColumns.resolveColumnLayouts(
    columnsShown,
    useProposedLayout,
  )
  return TableColumns.resolveProposedCellRenderers(
    withLayouts,
    useProposedLayout,
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default tradeBlotterColumnDefinitions
